import RightChevron from "@/assets/colorable/right_chevron.svg?react";
import clsxm from "@/lib/clsxm";
import {AnimatePresence, motion} from "framer-motion";
import {useState, type FC, type ReactNode} from "react";
import {useTranslation} from "react-i18next";
import {useMeasure} from "react-use";
import {useStateWithDeps} from "use-state-with-deps";

const TEXT_LINE_HEIGHT = 20;
const SHOWN_LINES_LIMIT = 5;
const ANIMATION_DURATION = 400;

const CollapsibleContent: FC<{
  animationDuration?: number;
  children: ReactNode;
  className?: string;
  dataTestId?: string;
  expanded?: boolean;
  expandedMaxHeight?: number;
  fadeClassName?: string;
  lineHeight?: number;
  longerContentClassName?: string;
  onExpand?: (isExpanded: boolean) => void;
  openedTextHeight?: number;
  shownLinesLimit?: number;
  viewMoreClassName?: string;
}> = ({
  animationDuration = ANIMATION_DURATION,
  children,
  className,
  dataTestId,
  expanded,
  expandedMaxHeight,
  fadeClassName,
  lineHeight = TEXT_LINE_HEIGHT,
  longerContentClassName,
  onExpand,
  openedTextHeight,
  shownLinesLimit = SHOWN_LINES_LIMIT,
  viewMoreClassName,
}) => {
  const {t} = useTranslation("common");
  const [contentRef, {height: measuredHeight}] = useMeasure<HTMLDivElement>();
  const [isExpanded, setIsExpanded] = useStateWithDeps(expanded ?? false, [expanded]);
  const [hasUserToggled, setHasUserToggled] = useState(false);

  const openedHeight = measuredHeight || openedTextHeight || 0;
  const isContentLongerThanLimit = openedHeight / lineHeight > shownLinesLimit;
  const expandedHeight = expandedMaxHeight ? Math.min(openedHeight, expandedMaxHeight) : openedHeight;
  const isExpandedScrollable = isExpanded && !!expandedMaxHeight && openedHeight > expandedMaxHeight;

  return (
    <div className={clsxm("flex flex-col gap-2", className, isContentLongerThanLimit && longerContentClassName)}>
      <div className="relative">
        <AnimatePresence initial={false}>
          <motion.div
            animate={{
              maxHeight: isExpanded ? expandedHeight : shownLinesLimit * lineHeight,
            }}
            transition={{duration: hasUserToggled ? animationDuration * 0.001 : 0, ease: "easeInOut"}}
            className={isExpandedScrollable ? "overflow-x-hidden overflow-y-auto" : "overflow-hidden"}>
            <div
              ref={contentRef}
              className="text-neutral-black text-sm whitespace-pre-line"
              data-testid={dataTestId}
              style={{lineHeight: `${lineHeight}px`}}>
              {children}
            </div>
          </motion.div>
        </AnimatePresence>
        {!isExpanded && isContentLongerThanLimit && (
          <div
            className={clsxm(
              "pointer-events-none absolute right-0 bottom-0 left-0 h-4 bg-linear-to-t from-white to-transparent",
              fadeClassName,
            )}
          />
        )}
      </div>
      {isContentLongerThanLimit && (
        <button
          onClick={() => {
            setHasUserToggled(true);

            if (onExpand) {
              onExpand(!expanded);
            } else {
              setIsExpanded(!isExpanded);
            }
          }}
          className={clsxm(
            "text-neutral-black flex items-center justify-center gap-2.5 text-sm font-medium",
            viewMoreClassName,
          )}>
          {t(isExpanded ? "view_less" : "view_more")}
          <RightChevron className={clsxm("h-2.5 transition-all", isExpanded ? "-rotate-90" : "rotate-90")} />
        </button>
      )}
    </div>
  );
};

export default CollapsibleContent;
